import { motion } from 'framer-motion';
import { MapPin, Search, Shield, Sparkles, Star, Users } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  ease,
  fadeUp,
  fadeUpSmall,
  heroHeadline,
  heroSub,
  staggerParent,
} from '../../lib/animations';

const popularCities = ['London', 'Dubai', 'New York', 'Tokyo', 'Paris'];

const nightWorldMapUrl =
  'https://eoimages.gsfc.nasa.gov/images/imagerecords/55000/55167/earth_lights_lrg.jpg';

const priceTags = [
  { label: '$1.8k', className: 'left-[31%] top-[34%]' },
  { label: '£920', className: 'left-[58%] top-[27%]' },
  { label: 'NPR 28k', className: 'left-[80%] top-[36%]' },
  { label: 'AED 3.2k', className: 'left-[70%] top-[49%]' },
  { label: '$2.4k', className: 'right-[4%] top-[42%]' },
];

const glowNodes = [
  { className: 'left-[33%] top-[39%]' },
  { className: 'left-[59%] top-[34%]' },
  { className: 'left-[79%] top-[43%]' },
  { className: 'left-[71%] top-[56%]' },
  { className: 'right-[6%] top-[50%]' },
  { className: 'left-[45%] top-[67%]' },
];

function RouteArcLayer() {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 1440 820"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="heroRouteBlue" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
          <stop offset="42%" stopColor="#60a5fa" stopOpacity="0.92" />
          <stop offset="100%" stopColor="#93c5fd" stopOpacity="0.1" />
        </linearGradient>
        <filter id="heroRouteGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="0 0 0 0 0.16 0 0 0 0 0.48 0 0 0 0 1 0 0 0 0.85 0"
            result="glow"
          />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g className="hero-route-layer" filter="url(#heroRouteGlow)">
        <path className="hero-route hero-route-a" d="M504 322 C628 210 748 178 878 292" />
        <path className="hero-route hero-route-b" d="M872 309 C1006 217 1114 245 1248 371" />
        <path className="hero-route hero-route-c" d="M1002 439 C1118 372 1228 372 1346 494" />
        <path className="hero-route hero-route-d" d="M462 632 C610 475 796 431 1009 477" />
        <path className="hero-route hero-route-e" d="M1088 484 C1232 530 1324 606 1385 724" />
      </g>
    </svg>
  );
}

function PriceTag({ label, className, index }) {
  return (
    <div
      className={`hero-map-price pointer-events-none absolute hidden items-center gap-1.5 rounded-full border border-white/85 bg-white px-3.5 py-2 text-sm font-extrabold text-slate-950 shadow-[0_18px_50px_rgba(2,6,23,0.36)] md:flex ${className}`}
      style={{ animationDelay: `${index * 0.45}s` }}
    >
      <MapPin className="h-4 w-4 fill-rose-500/10 text-rose-500" />
      <span>{label}</span>
    </div>
  );
}

function GlowNode({ className, index }) {
  return (
    <span
      className={`hero-map-node pointer-events-none absolute hidden h-3 w-3 rounded-full bg-sky-200 shadow-[0_0_24px_rgba(56,189,248,0.95)] md:block ${className}`}
      style={{ animationDelay: `${index * 0.55}s` }}
    />
  );
}

export default function RentalNetworkHero({
  searchQuery,
  setSearchQuery,
  searchFocused,
  setSearchFocused,
  handleSearch,
  navigate,
}) {
  return (
    <section className="real-map-hero relative isolate flex min-h-[88vh] items-center overflow-hidden bg-slate-950 pt-16 text-white md:min-h-[90vh] md:pt-[72px]">
      <style>{`
        .real-map-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: -7;
          pointer-events: none;
          background:
            radial-gradient(circle at 24% 22%, rgba(37, 99, 235, 0.18), transparent 29%),
            radial-gradient(circle at 73% 44%, rgba(14, 165, 233, 0.17), transparent 28%),
            linear-gradient(110deg, rgba(2, 6, 23, 0.96) 0%, rgba(3, 15, 31, 0.78) 46%, rgba(3, 22, 41, 0.84) 100%);
        }
        .hero-map-image {
          background-size: cover;
          background-position: center 47%;
          filter: saturate(1.18) contrast(1.14) brightness(0.84);
          transform: scale(1.035);
        }
        .hero-route {
          fill: none;
          stroke: url(#heroRouteBlue);
          stroke-width: 2.4;
          stroke-linecap: round;
          stroke-dasharray: 13 15;
          opacity: 0.82;
          animation: hero-route-flow 13s linear infinite;
        }
        .hero-route-a,
        .hero-route-c {
          stroke-width: 3.3;
          opacity: 0.95;
        }
        .hero-route-b {
          animation-delay: -2.5s;
        }
        .hero-route-c {
          animation-delay: -5s;
        }
        .hero-route-d {
          opacity: 0.54;
          animation-delay: -7s;
        }
        .hero-route-e {
          opacity: 0.6;
          animation-delay: -9s;
        }
        .hero-map-node::before,
        .hero-map-node::after {
          content: "";
          position: absolute;
          inset: -11px;
          border-radius: 999px;
          border: 1px solid rgba(96, 165, 250, 0.62);
          animation: hero-node-pulse 3.8s ease-out infinite;
        }
        .hero-map-node::after {
          inset: -21px;
          opacity: 0.42;
          animation-delay: 1.15s;
        }
        .hero-map-price {
          animation: hero-price-drift 6.8s ease-in-out infinite;
        }
        .hero-map-price::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -7px;
          width: 12px;
          height: 12px;
          transform: translateX(-50%) rotate(45deg);
          background: white;
          border-bottom: 1px solid rgba(255,255,255,0.85);
          border-right: 1px solid rgba(255,255,255,0.85);
          border-radius: 2px;
        }
        .hero-scan-light {
          animation: hero-scan-light 16s ease-in-out infinite;
        }
        @keyframes hero-route-flow {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -220; }
        }
        @keyframes hero-node-pulse {
          0% { transform: scale(0.5); opacity: 0.82; }
          78%, 100% { transform: scale(1.65); opacity: 0; }
        }
        @keyframes hero-price-drift {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50% { transform: translate3d(0, -5px, 0); }
        }
        @keyframes hero-scan-light {
          0%, 100% { transform: translate3d(-40%, 0, 0) rotate(11deg); opacity: 0; }
          24%, 46% { opacity: 0.5; }
          72% { transform: translate3d(118%, 0, 0) rotate(11deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-route,
          .hero-map-node::before,
          .hero-map-node::after,
          .hero-map-price,
          .hero-scan-light {
            animation-duration: 1ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 z-[-6]">
        <div
          className="hero-map-image absolute inset-0 opacity-90"
          style={{ backgroundImage: `url(${nightWorldMapUrl})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.96)_0%,rgba(2,6,23,0.90)_22%,rgba(2,6,23,0.48)_48%,rgba(2,6,23,0.14)_74%,rgba(2,6,23,0.52)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_63%_38%,rgba(37,99,235,0.16),transparent_24%),radial-gradient(circle_at_88%_49%,rgba(14,165,233,0.14),transparent_23%),linear-gradient(180deg,rgba(2,6,23,0.24)_0%,rgba(2,6,23,0.04)_45%,rgba(2,6,23,0.86)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(96,165,250,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(96,165,250,0.07)_1px,transparent_1px)] bg-[size:84px_84px] opacity-40 [mask-image:linear-gradient(90deg,transparent,black_14%,black_92%,transparent)]" />
        <RouteArcLayer />
        {glowNodes.map((node, index) => (
          <GlowNode key={node.className} className={node.className} index={index} />
        ))}
        {priceTags.map((tag, index) => (
          <PriceTag key={tag.label} label={tag.label} className={tag.className} index={index} />
        ))}
        <div className="hero-scan-light absolute top-0 h-full w-[30rem] bg-gradient-to-r from-transparent via-sky-200/10 to-transparent blur-sm" />
      </div>

      <motion.div className="rh-container-wide relative z-20 flex py-14">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerParent(0.08, 0.1)}
          className="max-w-[40rem] min-w-0"
        >
          <motion.span
            variants={fadeUpSmall}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-300/55 bg-slate-950/52 px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.16em] text-cyan-50 shadow-[0_14px_40px_rgba(8,47,73,0.28)] backdrop-blur-xl"
          >
            <Sparkles className="h-4 w-4 text-amber-200" />
            MAP-FIRST RENTAL DISCOVERY
          </motion.span>

          <motion.h1
            variants={heroHeadline}
            className="font-serif text-[2.65rem] leading-[1.02] text-white drop-shadow-[0_12px_42px_rgba(0,0,0,0.62)] sm:text-[3.6rem] md:text-[4.55rem] xl:text-[5.2rem]"
          >
            Find Your
            <br />
            Perfect
            <br />
            Space
            <br />
            Worldwide
          </motion.h1>

          <motion.p
            variants={heroSub}
            className="mt-6 max-w-xl text-base leading-8 text-slate-100/90 drop-shadow-[0_1px_12px_rgba(2,6,23,0.55)] md:text-lg"
          >
            Discover verified rooms, flats, and rental spaces with transparent
            prices on a live map, with no confusion and no hidden surprises.
          </motion.p>

          <motion.form
            variants={fadeUp}
            onSubmit={handleSearch}
            className="relative mb-7 mt-9 w-full max-w-[calc(100vw-3rem)] min-w-0 sm:max-w-2xl"
          >
            <motion.div
              animate={{
                boxShadow: searchFocused
                  ? '0 30px 70px -24px rgba(59,130,246,0.58), 0 0 0 4px rgba(96,165,250,0.18)'
                  : '0 22px 56px -22px rgba(2,6,23,0.72)',
                scale: searchFocused ? 1.01 : 1,
              }}
              transition={{ duration: 0.35, ease: ease.standard }}
              className="relative flex w-full min-w-0 flex-col gap-2 rounded-[2rem] border border-white/28 bg-slate-950/66 p-2 shadow-2xl backdrop-blur-xl sm:flex-row sm:items-center sm:rounded-full"
            >
              <div className="relative min-w-0 flex-1">
                <div
                  className={`pointer-events-none absolute left-4 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center transition-colors duration-300 ${
                    searchFocused ? 'text-blue-300' : 'text-slate-400'
                  }`}
                >
                  <Search className="h-5 w-5" />
                </div>

                <Input
                  type="text"
                  placeholder="Search by city, country, or location"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  className="h-14 w-full rounded-full border-0 bg-transparent pl-12 pr-4 text-sm text-white placeholder:text-slate-300/65 sm:text-base md:text-lg focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="hero-search-input"
                />
              </div>

              <motion.div
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
                className="shrink-0"
              >
                <Button
                  type="submit"
                  className="btn-shine h-12 w-full rounded-full bg-blue-600 px-8 text-base font-semibold text-white hover:bg-blue-500 sm:w-auto"
                  data-testid="hero-search-btn"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              variants={fadeUpSmall}
              className="mt-5 grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center"
            >
              <span className="col-span-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100/72 sm:col-span-1">
                Popular
              </span>
              {popularCities.map((city) => (
                <motion.button
                  key={city}
                  type="button"
                  onClick={() => navigate(`/search?city=${encodeURIComponent(city)}`)}
                  whileHover={{ y: -2, scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ duration: 0.2 }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm backdrop-blur-md transition-colors sm:px-3.5 ${
                    city === 'London'
                      ? 'border-blue-400 bg-blue-600 text-white hover:bg-blue-500'
                      : 'border-white/24 bg-white/8 text-white hover:border-cyan-200/60 hover:bg-cyan-200/14'
                  }`}
                >
                  {city}
                </motion.button>
              ))}
            </motion.div>
          </motion.form>

          <motion.div variants={fadeUp} className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
              <Button
                onClick={() => navigate('/search')}
                className="btn-shine group relative w-full justify-center rounded-full bg-white px-8 py-6 text-base font-semibold text-slate-950 shadow-[0_16px_46px_-18px_rgba(59,130,246,0.72)] transition-all hover:bg-white sm:w-auto md:text-lg"
                data-testid="explore-map-btn"
              >
                <MapPin className="mr-2 h-6 w-6 text-blue-600" />
                Explore on Map
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }} transition={{ duration: 0.2 }}>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full justify-center rounded-full border-white/32 bg-white/5 px-8 py-6 text-base font-semibold text-white shadow-md backdrop-blur-md transition-all hover:border-white/70 hover:bg-white/15 sm:w-auto md:text-lg"
                data-testid="list-property-btn"
              >
                List Your Property
              </Button>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeUpSmall} className="mt-8 grid gap-2 text-white/88 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-3">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Shield className="h-4 w-4 text-cyan-200" /> Verified owners
            </div>
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Star className="h-4 w-4 fill-amber-200 text-amber-200" /> 4.9 avg rating
            </div>
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Users className="h-4 w-4 text-blue-200" /> 2,400+ reviews
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
